
import React, { useState, useEffect } from 'react';
import { Globe, Mail, Lock, LogIn, ArrowRight, Eye, EyeOff, Loader2, User as UserIcon, X, ArrowLeft, CheckCircle2 } from 'lucide-react';
import { supabase } from '../supabase';
import { User } from '../types';

interface LoginProps {
  users: User[];
  onSwitchToRegister: () => void;
}

interface SavedProfile {
  email: string;
  name: string;
  password?: string;
}

const STORAGE_KEY = 'cabinet_quick_access_profiles';

const Login: React.FC<LoginProps> = ({ users, onSwitchToRegister }) => {
  const [view, setView] = useState<'login' | 'forgot'>('login');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [rememberMe, setRememberMe] = useState(true);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  const [successMsg, setSuccessMsg] = useState<string | null>(null);
  const [savedProfiles, setSavedProfiles] = useState<SavedProfile[]>([]);

  useEffect(() => {
    const saved = localStorage.getItem(STORAGE_KEY);
    if (saved) {
      try {
        setSavedProfiles(JSON.parse(saved));
      } catch (e) {
        console.error("Erro ao carregar perfis salvos");
      }
    }
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setErrorMsg(null);
    try {
      const { data, error } = await supabase.auth.signInWithPassword({ email, password });
      if (error) throw error;

      if (rememberMe && data.user) {
        saveProfile(email, data.user.user_metadata?.full_name || email.split('@')[0], password);
      }
    } catch (err: any) {
      console.error("Erro no login:", err);
      let msg = err.message;
      if (msg === 'Invalid login credentials') msg = 'E-mail ou senha inválidos.';
      if (msg === 'Email not confirmed') msg = 'E-mail não confirmado. Verifique sua caixa de entrada.';
      setErrorMsg(msg);
      setIsSubmitting(false); 
    }
  };

  const handleForgotPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email) return;
    setIsSubmitting(true);
    setErrorMsg(null);
    setSuccessMsg(null);

    try {
      const { error } = await supabase.auth.resetPasswordForEmail(email, {
        redirectTo: window.location.origin,
      });
      if (error) throw error;
      setSuccessMsg("Link de recuperação enviado para o seu e-mail.");
    } catch (err: any) {
      setErrorMsg(err.message || "Erro ao solicitar recuperação.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const saveProfile = (email: string, name: string, pass: string) => {
    let current = [...savedProfiles];
    const index = current.findIndex(p => p.email === email);
    const newProfile = { email, name, password: pass };
    if (index > -1) {
      current[index] = newProfile;
    } else {
      current.unshift(newProfile);
    }
    const limited = current.slice(0, 3);
    setSavedProfiles(limited);
    localStorage.setItem(STORAGE_KEY, JSON.stringify(limited));
  };

  const removeProfile = (e: React.MouseEvent, emailToRemove: string) => {
    e.stopPropagation();
    const updated = savedProfiles.filter(p => p.email !== emailToRemove);
    setSavedProfiles(updated);
    localStorage.setItem(STORAGE_KEY, JSON.stringify(updated));
    if (email === emailToRemove) {
      setEmail('');
      setPassword('');
    }
  };

  const handleQuickLogin = (profile: SavedProfile) => {
    setEmail(profile.email);
    if (profile.password) {
      setPassword(profile.password);
    } else {
      setPassword('');
      const pwdField = document.getElementById('password-field');
      if (pwdField) pwdField.focus();
    }
    setView('login');
  };

  return (
    <div className="min-h-screen bg-white dark:bg-navy-950 flex items-center justify-center p-6 font-medium transition-colors">
      <div className="w-full max-w-[420px] animate-in fade-in zoom-in-95 duration-500">
        
        {view === 'login' && savedProfiles.length > 0 && (
          <div className="mb-8 animate-in slide-in-from-top-4 duration-700">
            <p className="text-[10px] font-black text-neutral-400 dark:text-slate-500 uppercase tracking-widest text-center mb-4">Acesso Rápido</p>
            <div className="flex justify-center gap-6">
              {savedProfiles.map((profile) => (
                <div 
                  key={profile.email} 
                  onClick={() => handleQuickLogin(profile)}
                  className="group relative flex flex-col items-center gap-2 cursor-pointer"
                >
                  <button 
                    onClick={(e) => removeProfile(e, profile.email)}
                    className="absolute -top-1 -right-1 z-10 bg-white dark:bg-navy-800 border border-neutral-200 dark:border-navy-700 text-neutral-400 hover:text-red-500 rounded-full p-1 opacity-0 group-hover:opacity-100 transition-all shadow-sm"
                  >
                    <X size={10} />
                  </button>
                  <div className={`w-14 h-14 rounded-2xl border-2 transition-all flex items-center justify-center font-black text-lg ${email === profile.email ? 'bg-black text-white border-black shadow-xl dark:bg-parliament-600 dark:border-parliament-500' : 'bg-neutral-100 dark:bg-navy-900 border-transparent text-neutral-400 dark:text-slate-600 group-hover:border-neutral-200 dark:group-hover:border-navy-700'}`}>
                    {profile.name.charAt(0).toUpperCase()}
                  </div>
                  <span className="text-[10px] font-bold text-neutral-500 dark:text-slate-400 group-hover:text-black dark:group-hover:text-white truncate max-w-[60px]">{profile.name.split(' ')[0]}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        <div className="bg-white dark:bg-navy-900 border border-neutral-200 dark:border-navy-800 rounded-[32px] p-10 shadow-2xl shadow-neutral-100 dark:shadow-none relative overflow-hidden transition-colors">
          <div className="flex flex-col items-center text-center mb-10">
            <div className="bg-black dark:bg-parliament-600 p-4 rounded-2xl text-white shadow-xl mb-6"><Globe size={32} /></div>
            <h1 className="text-3xl font-black text-black dark:text-white tracking-tighter">Gabinete Digital</h1>
            <p className="text-neutral-400 dark:text-slate-500 mt-2 text-sm font-medium">
              {view === 'login' ? 'Acesse sua plataforma parlamentar' : 'Recuperação de Acesso'}
            </p>
          </div>

          {view === 'login' ? (
            <form onSubmit={handleSubmit} className="space-y-6">
              {errorMsg && (
                <div className="p-4 bg-red-50 dark:bg-red-950/30 border border-red-100 dark:border-red-900/50 rounded-2xl text-red-600 dark:text-red-400 text-[10px] font-black uppercase tracking-widest animate-in slide-in-from-top-2">
                  {errorMsg}
                </div>
              )}
              
              <div className="space-y-1.5">
                <label className="text-[10px] font-black text-neutral-400 dark:text-slate-500 uppercase tracking-widest ml-4">Identificação</label>
                <div className="relative">
                  <Mail className="absolute left-5 top-1/2 -translate-y-1/2 text-neutral-300 dark:text-navy-700" size={18} />
                  <input 
                    required 
                    type="email" 
                    placeholder="nome@exemplo.com" 
                    value={email} 
                    onChange={(e) => setEmail(e.target.value)} 
                    className="w-full bg-neutral-50 dark:bg-navy-950 border border-neutral-100 dark:border-navy-800 rounded-2xl pl-14 pr-6 py-4 text-sm font-bold focus:ring-2 focus:ring-black dark:focus:ring-parliament-600 focus:bg-white dark:focus:bg-navy-950 outline-none transition-all dark:text-white" 
                  />
                </div>
              </div>

              <div className="space-y-1.5">
                <label className="text-[10px] font-black text-neutral-400 dark:text-slate-500 uppercase tracking-widest ml-4">Senha</label>
                <div className="relative">
                  <Lock className="absolute left-5 top-1/2 -translate-y-1/2 text-neutral-300 dark:text-navy-700" size={18} />
                  <input 
                    id="password-field"
                    required 
                    type={showPassword ? "text" : "password"} 
                    value={password} 
                    onChange={(e) => setPassword(e.target.value)} 
                    className="w-full bg-neutral-50 dark:bg-navy-950 border border-neutral-100 dark:border-navy-800 rounded-2xl pl-14 pr-14 py-4 text-sm font-bold focus:ring-2 focus:ring-black dark:focus:ring-parliament-600 focus:bg-white dark:focus:bg-navy-950 outline-none transition-all dark:text-white" 
                  />
                  <button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute right-5 top-1/2 -translate-y-1/2 text-neutral-300 dark:text-navy-700 hover:text-black dark:hover:text-white transition-colors">{showPassword ? <EyeOff size={18} /> : <Eye size={18} />}</button>
                </div>
              </div>

              <div className="flex items-center justify-between px-2">
                <label className="flex items-center gap-3 cursor-pointer group">
                  <div className="relative flex items-center">
                    <input 
                      type="checkbox" 
                      checked={rememberMe}
                      onChange={(e) => setRememberMe(e.target.checked)}
                      className="peer appearance-none w-5 h-5 border-2 border-neutral-200 dark:border-navy-800 rounded-lg checked:bg-black dark:checked:bg-parliament-600 checked:border-black dark:checked:border-parliament-600 transition-all" 
                    />
                    <div className="absolute inset-0 flex items-center justify-center text-white opacity-0 peer-checked:opacity-100 transition-opacity pointer-events-none">
                      <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="4" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"></polyline></svg>
                    </div>
                  </div>
                  <span className="text-[10px] font-black text-neutral-400 dark:text-slate-500 uppercase tracking-widest group-hover:text-black dark:group-hover:text-white transition-colors">Lembrar de mim</span>
                </label>
                <button 
                  type="button" 
                  onClick={() => { setView('forgot'); setErrorMsg(null); setSuccessMsg(null); }}
                  className="text-[10px] font-black text-neutral-300 dark:text-navy-700 hover:text-black dark:hover:text-white uppercase tracking-widest transition-colors"
                >
                  Esqueci a senha
                </button>
              </div>

              <button disabled={isSubmitting} type="submit" className="w-full bg-black dark:bg-parliament-600 text-white rounded-2xl py-5 font-black text-xs uppercase tracking-[0.2em] shadow-lg shadow-black/5 flex items-center justify-center gap-3 transition-all hover:scale-[1.01] active:scale-[0.98] disabled:opacity-50">
                {isSubmitting ? <Loader2 className="animate-spin" size={20} /> : <>ENTRAR NO SISTEMA <ArrowRight size={18} /></>}
              </button>
            </form>
          ) : (
            <form onSubmit={handleForgotPassword} className="space-y-6 animate-in slide-in-from-right-4">
              <button 
                type="button" 
                onClick={() => setView('login')}
                className="flex items-center gap-2 text-[10px] font-black text-neutral-400 uppercase tracking-widest hover:text-black transition-all mb-2"
              >
                <ArrowLeft size={14} /> Voltar para login
              </button>

              {errorMsg && (
                <div className="p-4 bg-red-50 dark:bg-red-950/30 border border-red-100 dark:border-red-900/50 rounded-2xl text-red-600 dark:text-red-400 text-[10px] font-black uppercase tracking-widest">
                  {errorMsg}
                </div>
              )}

              {successMsg ? (
                <div className="p-8 bg-emerald-50 dark:bg-emerald-950/30 border border-emerald-100 dark:border-emerald-900/50 rounded-[28px] text-center space-y-4 animate-in zoom-in-95">
                  <div className="w-12 h-12 bg-emerald-100 dark:bg-emerald-900/50 text-emerald-600 rounded-full flex items-center justify-center mx-auto"><CheckCircle2 size={24} /></div>
                  <div>
                    <p className="text-emerald-800 dark:text-emerald-400 font-black text-xs uppercase tracking-widest">E-mail Enviado</p>
                    <p className="text-emerald-600 dark:text-emerald-500/80 text-xs font-medium mt-1 leading-relaxed">Verifique sua caixa de entrada e spam para redefinir sua senha.</p>
                  </div>
                  <button type="button" onClick={() => setView('login')} className="text-[10px] font-black text-emerald-600 uppercase tracking-widest underline underline-offset-4 mt-2">Ir para Login</button>
                </div>
              ) : (
                <>
                  <div className="space-y-1.5">
                    <label className="text-[10px] font-black text-neutral-400 dark:text-slate-500 uppercase tracking-widest ml-4">E-mail da Conta</label>
                    <div className="relative">
                      <Mail className="absolute left-5 top-1/2 -translate-y-1/2 text-neutral-300 dark:text-navy-700" size={18} />
                      <input 
                        required 
                        type="email" 
                        placeholder="seu@email.com" 
                        value={email} 
                        onChange={(e) => setEmail(e.target.value)} 
                        className="w-full bg-neutral-50 dark:bg-navy-950 border border-neutral-100 dark:border-navy-800 rounded-2xl pl-14 pr-6 py-4 text-sm font-bold focus:ring-2 focus:ring-black dark:focus:ring-parliament-600 focus:bg-white dark:focus:bg-navy-950 outline-none transition-all dark:text-white" 
                      />
                    </div>
                  </div>

                  <p className="text-[10px] text-slate-400 dark:text-slate-500 font-medium px-2 leading-relaxed">
                    Enviaremos um link seguro para o seu e-mail cadastrado para que você possa criar uma nova senha.
                  </p>

                  <button disabled={isSubmitting} type="submit" className="w-full bg-black dark:bg-parliament-600 text-white rounded-2xl py-5 font-black text-xs uppercase tracking-[0.2em] shadow-lg shadow-black/5 flex items-center justify-center gap-3 transition-all hover:scale-[1.01] active:scale-[0.98] disabled:opacity-50">
                    {isSubmitting ? <Loader2 className="animate-spin" size={20} /> : <>ENVIAR LINK DE ACESSO <ArrowRight size={18} /></>}
                  </button>
                </>
              )}
            </form>
          )}

          <div className="mt-8 pt-8 border-t border-neutral-100 dark:border-navy-800 text-center">
             <button onClick={onSwitchToRegister} className="text-[10px] font-black text-neutral-400 dark:text-slate-500 hover:text-black dark:hover:text-white uppercase tracking-widest transition-colors underline underline-offset-4 decoration-neutral-200 dark:decoration-navy-800">Criar Nova Conta</button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Login;
